import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation, Trans } from "react-i18next";
import { useSelector } from "react-redux";
import {
  Box,
  Button,
  Typography,
  LinearProgress,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import GameScreenShell from "@components/Home/Games/GameScreenShell";
import SessionSummary, {
  SessionSummaryStats,
} from "@components/Home/Games/SessionSummary";
import type { GameSessionGame } from "@models/game";
import { GAME_IDS } from "@models/game";
import {
  useCreateSessionMutation,
  useSubmitRoundMutation,
  useCompleteSessionMutation,
} from "@store/gameApi";
import { RootState } from "@store/store";
import {
  getLevelParams,
  resolveTargetAndDistractors,
  spawnObjects,
  createRespawnedTarget,
  drawShape,
  type LevelParams,
  type MovingObject,
  type ShapeId,
  type TargetAndDistractors,
} from "./targetFocusContent";
import { gameContinueButtonSx } from "@components/Home/Games/gameUi";

export interface TargetFocusGuidedProps {
  guidedSessionId?: string;
  guidedCurrentGame?: GameSessionGame;
  guidedGameIndex?: number;
  guidedTotalGames?: number;
  onGuidedGameComplete?: () => void;
}

const ROUNDS_PER_SESSION = 3;
const SESSION_START_DEDUPE_MS = 2000;
const HIT_RADIUS_PX = 40;

let lastSessionStartAt = 0;

type Phase = "idle" | "playing" | "feedback";

interface FreePlayLocationState {
  freePlaySessionId?: string;
  freePlayGames?: GameSessionGame[];
}

const TargetFocus: FC<TargetFocusGuidedProps> = ({
  guidedSessionId,
  guidedCurrentGame,
  guidedGameIndex = 0,
  guidedTotalGames = 1,
  onGuidedGameComplete,
}) => {
  const location = useLocation();
  const freePlayState = location.state as FreePlayLocationState | null;
  const freePlaySessionId = freePlayState?.freePlaySessionId;
  const freePlayGame = freePlayState?.freePlayGames?.[0];

  const isGuided = Boolean(guidedSessionId && guidedCurrentGame);
  const isFreePlayFromLibrary = Boolean(freePlaySessionId && freePlayGame);
  const effectiveSessionId = guidedSessionId ?? freePlaySessionId ?? null;
  const effectiveGame = guidedCurrentGame ?? freePlayGame;

  const { t } = useTranslation();
  const navigate = useNavigate();
  const selectedPatientId = useSelector(
    (state: RootState) => state.home.selectedPatientId
  );
  const sessionStartedRef = useRef(false);
  const [createSession] = useCreateSessionMutation();
  const [sessionStartError, setSessionStartError] = useState<string | null>(
    null
  );
  const [submitRound] = useSubmitRoundMutation();
  const [completeSession] = useCompleteSessionMutation();
  const contentSeed = effectiveGame?.contentSeed ?? "";
  const difficulty = effectiveGame
    ? Math.max(1, Math.min(10, effectiveGame.difficulty))
    : 1;
  const levelParams = useMemo<LevelParams>(
    () => getLevelParams(difficulty),
    [difficulty]
  );

  const [sessionId, setSessionId] = useState<string | null>(effectiveSessionId);
  const [phase, setPhase] = useState<Phase>("idle");
  const [roundCount, setRoundCount] = useState(0);
  const [roundStartTime, setRoundStartTime] = useState(0);
  const [roundTimeRemaining, setRoundTimeRemaining] = useState(0);
  const [targetShapeId, setTargetShapeId] = useState<string | null>(null);
  const [correctTaps, setCorrectTaps] = useState(0);
  const [falseTaps, setFalseTaps] = useState(0);
  const [tapCoordinates, setTapCoordinates] = useState<Array<{ x: number; y: number }>>([]);
  const [xpEarned, setXpEarned] = useState<number | null>(null);
  const [paused, setPaused] = useState(false);
  const [sessionStartTime, setSessionStartTime] = useState<number | null>(null);
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [sessionScore, setSessionScore] = useState(0);
  const [roundResults, setRoundResults] = useState<
    { accuracy: number; completionMs: number }[]
  >([]);
  const [domainUpdatesSum, setDomainUpdatesSum] = useState<
    Record<string, number>
  >({});
  const [showSessionSummary, setShowSessionSummary] = useState(false);
  const [summaryStats, setSummaryStats] = useState<SessionSummaryStats | null>(
    null
  );
  const [muted, setMuted] = useState(false);
  const [orangeFlash, setOrangeFlash] = useState(false);
  const submittedForRoundRef = useRef(false);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const objectsRef = useRef<MovingObject[]>([]);
  const targetAndDistractorsRef = useRef<TargetAndDistractors | null>(null);
  const animationRef = useRef<number>(0);
  const lastFrameRef = useRef<number>(0);
  const pendingRespawnsRef = useRef<Array<{ at: number; targetShapeId: ShapeId }>>(
    []
  );
  const respawnIdRef = useRef(0);
  const initialTargetCountRef = useRef(0);

  const startNewRound = useCallback(
    (roundIndex?: number) => {
      submittedForRoundRef.current = false;
      const idx = roundIndex ?? roundCount;
      const seed = contentSeed
        ? `${contentSeed}-${idx}`
        : `local-${Date.now()}-${idx}`;
      const { targetShapeId: tId, distractorShapeIds: dIds } =
        resolveTargetAndDistractors(seed, difficulty);
      targetAndDistractorsRef.current = { targetShapeId: tId, distractorShapeIds: dIds };
      setTargetShapeId(tId);

      const canvas = canvasRef.current;
      const w = canvas?.parentElement?.clientWidth ?? 400;
      const h = canvas?.parentElement?.clientHeight ?? 300;
      const objs = spawnObjects(seed, difficulty, w, h, tId, dIds);
      objectsRef.current = objs;
      initialTargetCountRef.current = objs.filter((o) => o.type === "target").length;
      pendingRespawnsRef.current = [];
      respawnIdRef.current = 0;

      setCorrectTaps(0);
      setFalseTaps(0);
      setTapCoordinates([]);
      setRoundStartTime(Date.now());
      setRoundTimeRemaining(levelParams.roundDurationMs / 1000);
      setXpEarned(null);
      setPhase("playing");
    },
    [contentSeed, roundCount, difficulty, levelParams.roundDurationMs]
  );

  useEffect(() => {
    if (isGuided || isFreePlayFromLibrary) return;
    if (!selectedPatientId) {
      setSessionStartError(t("games.errors.onlyPatientCanPlay"));
      return;
    }
    if (sessionStartedRef.current) return;
    const now = Date.now();
    if (now - lastSessionStartAt < SESSION_START_DEDUPE_MS) return;
    lastSessionStartAt = now;
    sessionStartedRef.current = true;
    setSessionStartError(null);
    let mounted = true;
    createSession({
        patientId: selectedPatientId,
        sessionType: "freeplay",
        gameId: GAME_IDS.targetFocus,
      })
      .unwrap()
      .then((res) => {
        if (mounted && res?.sessionId) {
          setSessionId(res.sessionId);
          setSessionStartTime(Date.now());
        }
      })
      .catch((err: { status?: number }) => {
        if (mounted && err?.status === 403) {
          setSessionStartError(t("games.errors.onlyPatientCanPlay"));
        }
      });
    return () => {
      mounted = false;
    };
  }, [
    isGuided,
    isFreePlayFromLibrary,
    selectedPatientId,
    createSession,
    t,
  ]);

  useEffect(() => {
    if ((isGuided && guidedSessionId) || isFreePlayFromLibrary) {
      setSessionStartTime(Date.now());
    }
  }, [isGuided, guidedSessionId, isFreePlayFromLibrary]);

  useEffect(() => {
    if (sessionStartTime == null || paused) return;
    const id = setInterval(() => {
      setTimerSeconds(Math.floor((Date.now() - sessionStartTime) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [sessionStartTime, paused]);

  useEffect(() => {
    if (phase !== "playing" || paused) return;
    const interval = setInterval(() => {
      const elapsed = Date.now() - roundStartTime;
      const remaining = Math.max(
        0,
        levelParams.roundDurationMs / 1000 - Math.floor(elapsed / 1000)
      );
      setRoundTimeRemaining(remaining);
      if (remaining <= 0) clearInterval(interval);
    }, 200);
    return () => clearInterval(interval);
  }, [
    phase,
    paused,
    roundStartTime,
    levelParams.roundDurationMs,
  ]);

  // Game loop: requestAnimationFrame
  useEffect(() => {
    if (phase !== "playing" || paused) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const container = canvas.parentElement;
    if (!container) return;

    const dpr = window.devicePixelRatio || 1;
    const layoutW = container.clientWidth;
    const layoutH = container.clientHeight;
    canvas.width = layoutW * dpr;
    canvas.height = layoutH * dpr;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.scale(dpr, dpr);

    const levelP = getLevelParams(difficulty);
    const midRoundTime = levelParams.roundDurationMs / 2;

    const tick = (now: number) => {
      const dt = (now - lastFrameRef.current) / 1000;
      lastFrameRef.current = now;

      const elapsed = now - roundStartTime;
      if (elapsed >= levelParams.roundDurationMs) {
        return;
      }

      const objs = objectsRef.current;
      const toRespawn: MovingObject[] = [];

      for (const obj of objs) {
        if (obj.removedAt !== undefined) continue;
        obj.x += obj.vx * dt;
        obj.y += obj.vy * dt;

        let out = false;
        if (obj.x < -obj.radius * 2) {
          obj.x = layoutW + obj.radius;
          out = true;
        } else if (obj.x > layoutW + obj.radius * 2) {
          obj.x = -obj.radius;
          out = true;
        }
        if (obj.y < -obj.radius * 2) {
          obj.y = layoutH + obj.radius;
          out = true;
        } else if (obj.y > layoutH + obj.radius * 2) {
          obj.y = -obj.radius;
          out = true;
        }
        if (out) toRespawn.push(obj);

        if (
          levelP.sizeDecreaseMidRound &&
          obj.type === "target" &&
          obj.baseRadius !== undefined &&
          elapsed > midRoundTime
        ) {
          obj.radius = Math.max(14, obj.baseRadius * 0.7);
        }
        if (
          levelP.camouflageOpacity < 1 &&
          obj.type === "target" &&
          elapsed > midRoundTime
        ) {
          obj.opacity = levelP.camouflageOpacity;
        }
      }

      // Process scheduled respawns: add new targets so shapes stay available all round
      // Use Date.now() — respawn times are scheduled with Date.now(), but requestAnimationFrame's now is a different time base
      const nowMs = Date.now();
      const pending = pendingRespawnsRef.current;
      const toRemove: number[] = [];
      pending.forEach((p, i) => {
        if (p.at <= nowMs) {
          const newTarget = createRespawnedTarget(
            p.targetShapeId,
            layoutW,
            layoutH,
            levelP.speedPxPerSec,
            `respawn-${++respawnIdRef.current}`
          );
          objs.push(newTarget);
          toRemove.push(i);
        }
      });
      pendingRespawnsRef.current = pending.filter((_, i) => !toRemove.includes(i));

      ctx.clearRect(0, 0, layoutW, layoutH);
      for (const obj of objs) {
        if (obj.removedAt !== undefined) continue;
        drawShape(
          ctx,
          obj.shapeId,
          obj.x,
          obj.y,
          obj.radius,
          obj.opacity,
          obj.type === "target"
        );
      }

      animationRef.current = requestAnimationFrame(tick);
    };

    lastFrameRef.current = performance.now();
    animationRef.current = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(animationRef.current);
    };
  }, [
    phase,
    paused,
    roundStartTime,
    levelParams.roundDurationMs,
    difficulty,
  ]);

  const handleCanvasPointerDown = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      if (phase !== "playing") return;
      const canvas = canvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const layoutX = e.clientX - rect.left;
      const layoutY = e.clientY - rect.top;

      const objs = objectsRef.current;
      let hit: MovingObject | null = null;
      let minD = HIT_RADIUS_PX + 1;
      for (const obj of objs) {
        if (obj.removedAt !== undefined) continue;
        const dx = layoutX - obj.x;
        const dy = layoutY - obj.y;
        const d = Math.sqrt(dx * dx + dy * dy);
        if (d <= HIT_RADIUS_PX && d < minD) {
          minD = d;
          hit = obj;
        }
      }
      if (!hit) return;

      setTapCoordinates((prev) => [...prev, { x: layoutX, y: layoutY }]);

      if (hit.type === "target") {
        hit.removedAt = Date.now();
        setCorrectTaps((c) => c + 1);
        const targetShapeId = targetAndDistractorsRef.current?.targetShapeId;
        if (targetShapeId) {
          pendingRespawnsRef.current.push({
            at: Date.now() + 500,
            targetShapeId,
          });
        }
      } else {
        setFalseTaps((f) => f + 1);
        setOrangeFlash(true);
        setTimeout(() => setOrangeFlash(false), 300);
      }
    },
    [phase]
  );

  const submitCurrentRound = useCallback(() => {
    if (submittedForRoundRef.current) return;
    submittedForRoundRef.current = true;

    const objs = objectsRef.current;
    const remainingTargets = objs.filter(
      (o) => o.type === "target" && o.removedAt === undefined
    ).length;
    const missedTargets = remainingTargets;
    const total = correctTaps + falseTaps + missedTargets;
    const accuracy = total > 0 ? correctTaps / total : 0;
    const completionTimeMs = Date.now() - roundStartTime;

    setRoundResults((prev) => [...prev, { accuracy, completionMs: completionTimeMs }]);

    const rawData = {
      correctTaps,
      falseTaps,
      missedTargets,
      tapCoordinates,
      objectLog: objs.map((o) => ({
        id: o.id,
        type: o.type,
        shapeId: o.shapeId,
        removedAt: o.removedAt,
      })),
    };

    if (sessionId) {
      submitRound({
        sessionId,
        body: {
          gameId: GAME_IDS.targetFocus,
          roundNumber: roundCount + 1,
          difficulty,
          accuracy,
          reactionTimeMs: null,
          completionTimeMs,
          errorCount: falseTaps + missedTargets,
          rawData,
        },
      })
        .unwrap()
        .then((res) => {
          if (res?.xpEarned != null) {
            setXpEarned(res.xpEarned);
            setSessionScore((s) => s + res.xpEarned);
          }
          if (res?.domainUpdates && Object.keys(res.domainUpdates).length > 0) {
            setDomainUpdatesSum((prev) => {
              const next = { ...prev };
              for (const [k, v] of Object.entries(res.domainUpdates)) {
                next[k] = (next[k] ?? 0) + v;
              }
              return next;
            });
          }
        })
        .catch(() => {});
    } else {
      const xp = accuracy >= 0.8 ? 20 : 10;
      setXpEarned(xp);
      setSessionScore((s) => s + xp);
    }
    setPhase("feedback");
  }, [
    correctTaps,
    falseTaps,
    roundStartTime,
    roundCount,
    sessionId,
    difficulty,
    submitRound,
    tapCoordinates,
  ]);

  useEffect(() => {
    if (phase !== "playing" || roundTimeRemaining > 0) return;
    submitCurrentRound();
  }, [phase, roundTimeRemaining, submitCurrentRound]);

  const handleNextRound = useCallback(() => {
    const nextRound = roundCount + 1;
    if (nextRound >= ROUNDS_PER_SESSION) {
      if (isGuided && onGuidedGameComplete) {
        onGuidedGameComplete();
        return;
      }
      const allResults = [...roundResults];
      const totalRounds = allResults.length;
      const avgAccuracy =
        totalRounds > 0
          ? (allResults.reduce((s, r) => s + r.accuracy, 0) / totalRounds) * 100
          : 0;
      const avgCompletionSec =
        totalRounds > 0
          ? allResults.reduce((s, r) => s + r.completionMs, 0) /
            totalRounds /
            1000
          : 0;

      if (sessionId) {
        completeSession({ sessionId })
          .unwrap()
          .then((res) => {
            setSummaryStats({
              accuracyPercent: avgAccuracy,
              avgReactionSeconds: avgCompletionSec,
              xpEarned: res?.totalXp ?? sessionScore,
              domainChanges: domainUpdatesSum,
            });
          })
          .catch(() => {
            setSummaryStats({
              accuracyPercent: avgAccuracy,
              avgReactionSeconds: avgCompletionSec,
              xpEarned: sessionScore,
              domainChanges: domainUpdatesSum,
            });
          });
      } else {
        setSummaryStats({
          accuracyPercent: avgAccuracy,
          avgReactionSeconds: avgCompletionSec,
          xpEarned: sessionScore,
          domainChanges: domainUpdatesSum,
        });
      }
      lastSessionStartAt = 0;
      setSessionId(null);
      setShowSessionSummary(true);
      return;
    }
    setRoundCount(nextRound);
    startNewRound(nextRound);
  }, [
    roundCount,
    roundResults,
    sessionId,
    sessionScore,
    domainUpdatesSum,
    isGuided,
    onGuidedGameComplete,
    completeSession,
    startNewRound,
  ]);

  const handleExit = useCallback(() => {
    if (sessionId) completeSession({ sessionId }).catch(() => {});
    lastSessionStartAt = 0;
    navigate(-1);
  }, [sessionId, completeSession, navigate]);

  const handleSummaryContinue = useCallback(() => navigate(-1), [navigate]);

  const progress =
    phase === "idle"
      ? 0
      : (roundCount + (phase === "feedback" ? 1 : 0)) / ROUNDS_PER_SESSION;

  const feedbackText =
    phase === "feedback" ? t("games.targetFocus.feedbackGood") : null;

  const targetShapeLabel =
    targetShapeId != null
      ? t(`games.targetFocus.shapeNames.${targetShapeId}`)
      : "";

  if (showSessionSummary && summaryStats) {
    return (
      <SessionSummary
        stats={summaryStats}
        onContinue={handleSummaryContinue}
      />
    );
  }

  return (
    <GameScreenShell
      gameName={t("games.targetFocus.title")}
      level={difficulty}
      progress={progress}
      feedback={feedbackText}
      timerSeconds={sessionStartTime != null ? timerSeconds : null}
      score={sessionScore > 0 ? sessionScore : null}
      paused={paused}
      onBack={handleExit}
      onPause={() => setPaused((p) => !p)}
      gameIndex={isGuided ? guidedGameIndex : undefined}
      totalGames={isGuided ? guidedTotalGames : undefined}
      muted={muted}
      onMuteChange={setMuted}
    >
      {phase === "idle" && (
        <Box
          textAlign="center"
          py={4}
          flex={1}
          display="flex"
          flexDirection="column"
          justifyContent="center"
        >
          {sessionStartError && (
            <Typography color="error" sx={{ mb: 2 }}>
              {sessionStartError}
            </Typography>
          )}
          <Typography variant="h6" gutterBottom>
            {t("games.targetFocus.instructionsTitle")}
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            {t("games.targetFocus.instructions")}
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => startNewRound()}
            sx={gameContinueButtonSx}
          >
            {t("games.targetFocus.startRound")}
          </Button>
        </Box>
      )}

      {(phase === "playing" || phase === "feedback") && (
        <>
          <Typography
            variant="h5"
            fontWeight={700}
            sx={{ fontSize: "1.5rem", mb: 1.5, textAlign: "center" }}
          >
            <Trans
              i18nKey="games.targetFocus.tapOnlyPrompt"
              values={{ shape: targetShapeLabel }}
              components={{
                highlight: (
                  <Box
                    component="span"
                    sx={{
                      color: "primary.main",
                      fontWeight: 800,
                      textUnderlineOffset: 3,
                    }}
                  />
                ),
              }}
            />
          </Typography>

          {phase === "playing" && (
            <Box sx={{ mb: 1 }}>
              <LinearProgress
                variant="determinate"
                value={
                  (roundTimeRemaining / (levelParams.roundDurationMs / 1000)) *
                  100
                }
                color="primary"
                sx={{ height: 8, borderRadius: 1 }}
              />
              <Typography variant="caption" color="text.secondary">
                {t("games.targetFocus.timeRemaining", {
                  seconds: roundTimeRemaining,
                })}
              </Typography>
            </Box>
          )}

          <Box
            sx={{
              flex: 1,
              minHeight: { xs: "min(48vh, 420px)", sm: 320 },
              position: "relative",
              width: "100%",
              maxWidth: 600,
              mx: "auto",
              borderRadius: 2,
              overflow: "hidden",
              border: 1,
              borderColor: "divider",
              bgcolor: "action.hover",
            }}
          >
            <canvas
              ref={canvasRef}
              onPointerDown={handleCanvasPointerDown}
              style={{
                width: "100%",
                height: "100%",
                minHeight: "min(48vh, 420px)",
                display: "block",
                touchAction: "none",
                cursor: "pointer",
              }}
            />
            {orangeFlash && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  bgcolor: "warning.light",
                  opacity: 0.4,
                  pointerEvents: "none",
                  animation: "targetfocus-flash 0.3s ease-out",
                  "@keyframes targetfocus-flash": {
                    "0%": { opacity: 0.4 },
                    "100%": { opacity: 0 },
                  },
                }}
              />
            )}
          </Box>

          {phase === "feedback" && (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              gap={2}
              mt={2}
              mb={2}
            >
              {xpEarned != null && (
                <Typography variant="body2" color="primary.main">
                  +{xpEarned} XP
                </Typography>
              )}
              <Button
                variant="contained"
                size="large"
                onClick={handleNextRound}
                sx={gameContinueButtonSx}
              >
                {roundCount + 1 >= ROUNDS_PER_SESSION
                  ? t("games.targetFocus.finishSession")
                  : t("games.targetFocus.nextRound")}
              </Button>
            </Box>
          )}
        </>
      )}
    </GameScreenShell>
  );
};

export default TargetFocus;
